// Google Analytics and SEO Tracking Component
import { useEffect } from "react";

// Google Analytics 4 Configuration
const GA_MEASUREMENT_ID = "G-XXXXXXXXXX"; // Replace with your actual GA4 Measurement ID

// Initialize Google Analytics
export const initGA = () => {
  // Create gtag script
  const gtagScript = document.createElement("script");
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(gtagScript);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
    send_page_view: true,
  });

  // Make gtag globally available
  window.gtag = gtag;
};

// Track page views
export const trackPageView = (url, title) => {
  if (typeof window.gtag !== "undefined") {
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_location: url,
      page_title: title,
    });
  }
};

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (typeof window.gtag !== "undefined") {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track app downloads
export const trackDownload = (platform) => {
  trackEvent("download_app", "engagement", platform);

  // Enhanced ecommerce tracking for app downloads
  if (typeof window.gtag !== "undefined") {
    window.gtag("event", "purchase", {
      transaction_id: `download_${Date.now()}`,
      value: 0,
      currency: "USD",
      items: [
        {
          item_id: "tak_business_manager_app",
          item_name: "TAK Business Manager App",
          item_category: "Mobile App",
          item_variant: platform,
          quantity: 1,
          price: 0,
        },
      ],
    });
  }
};

// Track form submissions
export const trackFormSubmission = (formName) => {
  trackEvent("form_submit", "engagement", formName);
};

// Track scroll depth
export const trackScrollDepth = () => {
  let maxScrollDepth = 0;

  const updateScrollDepth = () => {
    const scrollDepth = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
    );

    if (scrollDepth > maxScrollDepth && scrollDepth % 25 === 0) {
      maxScrollDepth = scrollDepth;
      trackEvent("scroll", "engagement", `${scrollDepth}%`);
    }
  };

  window.addEventListener("scroll", updateScrollDepth, { passive: true });

  return () => window.removeEventListener("scroll", updateScrollDepth);
};

// Facebook Pixel initialization
export const initFacebookPixel = () => {
  const FACEBOOK_PIXEL_ID = "YOUR_FACEBOOK_PIXEL_ID"; // Replace with your pixel ID

  if (typeof window.fbq === "undefined") {
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );

    window.fbq("init", FACEBOOK_PIXEL_ID);
    window.fbq("track", "PageView");
  }
};

// Track Facebook events
export const trackFacebookEvent = (eventName, parameters = {}) => {
  if (typeof window.fbq !== "undefined") {
    window.fbq("track", eventName, parameters);
  }
};

// Microsoft Clarity initialization (free heatmaps and session recordings)
export const initMicrosoftClarity = () => {
  const CLARITY_PROJECT_ID = "YOUR_CLARITY_PROJECT_ID"; // Replace with your project ID

  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
};

// Hotjar initialization (user behavior analytics)
export const initHotjar = () => {
  const HOTJAR_ID = "YOUR_HOTJAR_ID"; // Replace with your Hotjar ID

  (function (h, o, t, j, a, r) {
    h.hj =
      h.hj ||
      function () {
        (h.hj.q = h.hj.q || []).push(arguments);
      };
    h._hjSettings = { hjid: HOTJAR_ID, hjsv: 6 };
    a = o.getElementsByTagName("head")[0];
    r = o.createElement("script");
    r.async = 1;
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
    a.appendChild(r);
  })(window, document, "https://static.hotjar.com/c/hotjar-", ".js?sv=");
};

// SEO and Analytics Hook
const useAnalytics = () => {
  useEffect(() => {
    try {
      // Initialize tracking services
      initGA();
      // initFacebookPixel(); // Uncomment when you have Facebook Pixel
      // initMicrosoftClarity(); // Uncomment when you have Clarity ID
      // initHotjar(); // Uncomment when you have Hotjar ID

      // Track initial page view
      trackPageView(window.location.href, document.title);

      // Track scroll depth
      const removeScrollListener = trackScrollDepth();

      // Track time on page
      const startTime = Date.now();
      const trackTimeOnPage = () => {
        try {
          const timeOnPage = Math.round((Date.now() - startTime) / 1000);
          trackEvent("time_on_page", "engagement", "seconds", timeOnPage);
        } catch (error) {
          console.warn("Error tracking time on page:", error);
        }
      };

      // Track when user leaves the page
      window.addEventListener("beforeunload", trackTimeOnPage);

      // Cleanup
      return () => {
        try {
          if (removeScrollListener) removeScrollListener();
          window.removeEventListener("beforeunload", trackTimeOnPage);
        } catch (error) {
          console.warn("Error during analytics cleanup:", error);
        }
      };
    } catch (error) {
      console.warn("Error initializing analytics:", error);
    }
  }, []);
};

// Enhanced event tracking for business app
export const trackBusinessAppEvents = {
  downloadClick: (platform) => {
    trackDownload(platform);
    trackFacebookEvent("Lead", { content_name: "App Download", platform });
  },

  signupNewsletter: (email) => {
    trackEvent("newsletter_signup", "engagement", "footer");
    trackFacebookEvent("Lead", { content_name: "Newsletter Signup" });
  },

  contactFormSubmit: (type) => {
    trackFormSubmission(`contact_${type}`);
    trackFacebookEvent("Contact", { content_name: type });
  },

  faqInteraction: (question) => {
    trackEvent("faq_click", "engagement", question);
  },

  featureView: (feature) => {
    trackEvent("feature_view", "engagement", feature);
  },

  roiCalculatorUse: (businessType) => {
    trackEvent("roi_calculator", "tool_usage", businessType);
  },

  testimonialView: (testimonial) => {
    trackEvent("testimonial_view", "engagement", testimonial);
  },
};

export default useAnalytics;
